/**
 * Tech-debt #4 — shared repos + Device-Model report helpers used by several
 * CSMS family handlers (deviceModel, smartCharging, monitoring, displayMessages,
 * network). Repos are constructed against the same `db` singleton the store
 * uses, so a fresh-tab refresh hydrates from the same Dexie state.
 */
import { errorMessage } from '@/lib/errorMessage';
import { db } from '@/persistence/dexie/db';
import {
  ChargingProfileRepositoryDexie,
  ConfigurationRepositoryDexie,
  DisplayMessageRepositoryDexie,
  LocalAuthListRepositoryDexie,
  LocalAuthListVersionRepositoryDexie,
  VariableValuesRepositoryDexie,
} from '@/persistence/dexie/repositories';
import type { VariableValueSnapshot } from '@/persistence/ports';
import type { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { paginateNotifyReport, type ReportData } from '@/protocol/v201/actions/notifyReport';
import { useStationStore } from '../useStationStore';

export const localAuthListRepo = new LocalAuthListRepositoryDexie(db);
export const localAuthListVersionRepo = new LocalAuthListVersionRepositoryDexie(db);
export const configRepo = new ConfigurationRepositoryDexie(db);
export const varValuesRepo = new VariableValuesRepositoryDexie(db);
export const displayMessageRepo = new DisplayMessageRepositoryDexie(db);
export const chargingProfileRepo = new ChargingProfileRepositoryDexie(db);

export const VALID_MONITOR_TYPES = [
  'UpperThreshold',
  'LowerThreshold',
  'Delta',
  'Periodic',
  'PeriodicClockAligned',
];

/**
 * Session 19b — match an (Actual, unqualified) Device Model row by component +
 * variable. The minimum seed is all-unqualified, so the lookup ignores
 * evseId/connectorId qualification for now.
 */
export function findVar(
  rows: readonly VariableValueSnapshot[],
  component: string,
  variable: string,
  attributeType = 'Actual',
): VariableValueSnapshot | undefined {
  return rows.find(
    (r) =>
      r.component === component &&
      r.variable === variable &&
      r.attributeType === attributeType &&
      r.evseId === 0 &&
      r.connectorId === 0,
  );
}

/**
 * True if an (unqualified) Variable exists at all, regardless of attributeType.
 * Lets Get/SetVariables distinguish a known Variable whose requested attribute
 * isn't present (→ NotSupportedAttributeType) from a genuinely unknown one
 * (→ UnknownVariable).
 */
export function variableExists(
  rows: readonly VariableValueSnapshot[],
  component: string,
  variable: string,
): boolean {
  return rows.some(
    (r) =>
      r.component === component &&
      r.variable === variable &&
      r.evseId === 0 &&
      r.connectorId === 0,
  );
}

/**
 * Session 19b — items-per-NotifyReport-page derived from the
 * OCPPCommCtrlr.MessageMaxReportLength Variable (interpreted as a max item
 * count — see paginateNotifyReport). Defaults to 1024 when missing.
 */
export function maxReportItems(rows: readonly VariableValueSnapshot[]): number {
  const row = findVar(rows, 'OCPPCommCtrlr', 'MessageMaxReportLength');
  const n = row ? Number(row.value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 1024;
}

/** Fire a paginated NotifyReport stream; best-effort (errors → lastError). */
export async function emitNotifyReports(
  adapter: OcppV201Adapter,
  requestId: number,
  reportData: readonly ReportData[],
  rows: readonly VariableValueSnapshot[],
): Promise<void> {
  const pages = paginateNotifyReport(requestId, reportData, maxReportItems(rows));
  try {
    for (const page of pages) {
      await adapter.notifyReport(page).response;
    }
  } catch (err) {
    useStationStore.setState({ lastError: errorMessage(err) });
  }
}
